import {
	getAllUsers,
	postReassignInvestor,
} from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';
import { reassignInvestorColumns } from '@dealstack/frontend/admin/pages/Manage/User/ManageInvestor/ReassignInvestorModal/ReassignInvestorModal';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useDebounceValue } from '@dealstack/frontend/shared/other/react/hooks/useDebounceValue';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';

export enum REASSIGN_INVESTOR_FORM_FIELDS {
	SEARCH_QUERY = 'search_query',
}

const EMPTY_DATA = [];

export const useReassignInvestorModalController = ({ investorId }: { investorId: string }) => {
	const toastAPI = useDSToast();
	const reassignInvestorsForm = useForm({
		mode: 'all',
		reValidateMode: 'onChange',
	});

	const searchValue = useWatch({
		control: reassignInvestorsForm.control,
		name: REASSIGN_INVESTOR_FORM_FIELDS.SEARCH_QUERY,
	});
	const searchQuery = useDebounceValue(searchValue, 500);
	const reassignInvestorMutation = useMutation({
		mutationFn: (variables: Parameters<typeof postReassignInvestor>[0]) =>
			postReassignInvestor(variables),
	});
	const getUsersRequest = useQuery({
		queryKey: [
			API_PATHS.GODMODE_USERS_ALL_GET,
			{
				searchQuery,
				searchLimit: '100',
			},
		],

		queryFn: () =>
			getAllUsers(API_PATHS.GODMODE_USERS_ALL_GET, {
				searchQuery,
				searchLimit: '100',
			}),
		meta: { errorToast: 'Data cannot be shown!' },
	});
	const onReassignInvestor = async ({ userId }: { userId: string }) => {
		const targetUser = getUsersRequest.data?.payload?.find((item) => item.id === userId);
		if (!targetUser) {
			toastAPI.error({
				message: 'Couldnt match id to user',
			});
			return;
		}
		await reassignInvestorMutation.mutateAsync({
			investorId,
			targetUserId: userId,
		});
		toastAPI.success({
			message: `Successfully reassigned Investor to ${targetUser.email}`,
		});
	};
	const tanstackTable = useReactTable({
		data: getUsersRequest.data?.payload ?? EMPTY_DATA,
		columns: reassignInvestorColumns({ onReassignInvestor }),
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	});
	const tableContainerRef = useRef<HTMLDivElement>(null);
	const { rows } = tanstackTable.getRowModel();
	const rowVirtualizer = useVirtualizer({
		getScrollElement: () => tableContainerRef.current,
		count: rows.length,
		estimateSize: () => 50,
		overscan: 10,
	});

	return {
		getUsersRequest,
		rowVirtualizer,
		tanstackTable,
		rows,
		tableContainerRef,
		reassignInvestorsForm,
	};
};
